import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchMacroSummary,
  fetchMarketIndexCoverage,
  refreshDecisionMarketData,
  removeMacroWatchlistIndex,
  getDecisionApiError
} from '../../services/decisionApi';
import MacroIndexGrid from './components/MacroIndexGrid';
import MacroWatchlistModal from './components/MacroWatchlistModal';
import { EquityRegimeSummary, MarketRateIndicatorPanel, MarketStressPanel } from './components/MarketFactorPanel';

const TABS = [
  { id: 'bond', label: '债市' },
  { id: 'equity', label: 'A股' }
];

const REFRESH_GROUPS = [
  {
    key: 'bond',
    title: '债券指数',
    detail: '6组中债财富指数，用于债市环境和业绩基准',
    latestDays: 15,
    historyDays: 120,
    codes: ['CBA00101_1Y', 'CBA00101', 'CBA_COMPOSITE', 'GOVT_BOND', 'CREDIT_BOND', 'NCD']
  },
  {
    key: 'equity',
    title: 'A股与转债指数',
    detail: '主要股指、科创/创业板和中证转债，东财失败时用 AkShare 兜底',
    latestDays: 15,
    historyDays: 120,
    codes: ['SSE', 'SZSE', 'CSI300', 'CSI500', 'CSI800', 'CSI1000', 'STAR100', 'SSE50', 'CHINEXT', 'CSI_CONVERT']
  },
  {
    key: 'rates',
    title: '资金面与收益率曲线',
    detail: 'FDR007、国债1Y/3Y/5Y/10Y、AAA存单和AAA/AA+中票',
    latestDays: 30,
    historyDays: 30,
    codes: ['FDR007', 'GOVT_YIELD_1Y', 'GOVT_YIELD_3Y', 'GOVT_YIELD_5Y', 'GOVT_YIELD_10Y', 'NCD_AAA_YIELD_1Y', 'MTN_AAA_YIELD_3Y', 'MTN_AAP_YIELD_3Y']
  }
];

function RefreshCoverage({ group, coverage }) {
  const rows = (coverage || []).filter((row) => group.codes.includes(row.index_code));
  const covered = rows.filter((row) => Number(row.row_count) > 0);
  const latest = covered.map((row) => row.latest_date).filter(Boolean).sort().reverse()[0] || '—';
  return <span>{covered.length}/{group.codes.length}项有数据 · 最新 {latest}</span>;
}

export default function MacroPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'equity' ? 'equity' : 'bond';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [coverage, setCoverage] = useState([]);
  const [refreshingKey, setRefreshingKey] = useState('');
  const [ingestMsg, setIngestMsg] = useState('');

  const isSuper = user?.portalRole === 'superadmin';

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await fetchMacroSummary();
      setData(summary);
      fetchMarketIndexCoverage()
        .then((result) => setCoverage(result.coverage || []))
        .catch(() => setCoverage([]));
    } catch (err) {
      setError(getDecisionApiError(err, '加载失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  function switchTab(next) {
    setSearchParams(next === 'bond' ? {} : { tab: next });
  }

  async function handleRemove(row) {
    if (!window.confirm(`从宏观列表移除「${row.name}」？`)) return;
    try {
      await removeMacroWatchlistIndex(row.code);
      await loadSummary();
    } catch (err) {
      setError(getDecisionApiError(err, '删除失败'));
    }
  }

  async function handleMarketRefresh(group, mode, days) {
    const taskKey = `${group}:${mode}`;
    setRefreshingKey(taskKey);
    setIngestMsg('');
    setError('');
    try {
      const res = await refreshDecisionMarketData(group, mode, days);
      setCoverage(res.coverage || []);
      const groupLabel = REFRESH_GROUPS.find((item) => item.key === group)?.title || group;
      setIngestMsg(`${groupLabel}${mode === 'history' ? '历史补齐' : '最新更新'}完成，页面已重新读取云端生产库。`);
      await loadSummary();
    } catch (err) {
      setError(getDecisionApiError(err, '行情更新失败'));
    } finally {
      setRefreshingKey('');
    }
  }

  const bond = data?.bond;
  const manageProps = isSuper ? { canManage: true, onRemove: handleRemove } : {};

  return (
    <div>
      <h1 className="decision-page-title">宏观环境</h1>
      <p className="decision-page-sub">
        全市场公共数据中心：债市晴雨、中债债指、资金面、收益率曲线、信用利差与A股指数；产品分析按属性引用相关因子
      </p>

      <div className="macro-tabs">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? 'macro-tab active' : 'macro-tab'}
            onClick={() => switchTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {isSuper ? (
        <div className="decision-card macro-refresh-console">
          <div className="macro-refresh-head">
            <div>
              <h3>市场数据更新</h3>
              <p>数据直接写入腾讯云生产库；“更新最近”用于日常，“补齐历史”用于首次上线或缺口修复。</p>
            </div>
            <span className="macro-refresh-safety">不读取本地测试库</span>
          </div>
          <div className="macro-refresh-grid">
            {REFRESH_GROUPS.map((group) => (
              <div className="macro-refresh-row" key={group.key}>
                <div>
                  <strong>{group.title}</strong>
                  <p>{group.detail}</p>
                  <small><RefreshCoverage group={group} coverage={coverage} /></small>
                </div>
                <div className="macro-refresh-actions">
                  <button
                    type="button"
                    className="decision-btn ghost"
                    disabled={!!refreshingKey}
                    onClick={() => handleMarketRefresh(group.key, 'latest', group.latestDays)}
                  >
                    {refreshingKey === `${group.key}:latest` ? '更新中…' : '更新最近'}
                  </button>
                  <button
                    type="button"
                    className="decision-btn"
                    disabled={!!refreshingKey}
                    onClick={() => handleMarketRefresh(group.key, 'history', group.historyDays)}
                  >
                    {refreshingKey === `${group.key}:history` ? '补齐中…' : `补齐近${group.historyDays}日`}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {ingestMsg ? <p className="macro-ingest-msg">{ingestMsg}</p> : null}
        </div>
      ) : null}

      {loading ? <p className="decision-muted">加载中…</p> : null}
      {error ? <p className="decision-error">{error}</p> : null}

      {!loading && tab === 'bond' ? (
        <>
          <div className="decision-card macro-bond-card">
            <h3>债市晴雨表</h3>
            {bond?.ok ? (
              <>
                <div className="macro-bond-hero">
                  <span className="macro-bond-emoji">{bond.weather_emoji}</span>
                  <div>
                    <div className="macro-bond-weather">综合 {bond.weather_label}</div>
                    <div className="decision-muted">
                      交易日 {bond.trade_date || '—'}
                      {bond.week ? ` · ${bond.week}` : ''}
                    </div>
                  </div>
                </div>

                <div className="macro-bond-segments">
                  {[bond.rate, bond.credit].filter(Boolean).map((seg) => (
                    <div className="macro-bond-segment" key={seg.label}>
                      <div className="macro-bond-segment-title">{seg.label}</div>
                      <div className="macro-bond-heat">
                        <span className="macro-heat-red">{seg.red}红</span>
                        <span className="macro-heat-green">{seg.green}绿</span>
                      </div>
                      <div className="macro-bond-segment-weather">
                        {seg.weather_emoji} {seg.weather_label}
                      </div>
                    </div>
                  ))}
                </div>

                <p className="decision-muted macro-source-note">
                  红/绿统计来自南方晴雨表热力格：红=债价上行，绿=债价下行
                </p>
                {bond.deadline_time ? (
                  <p className="decision-muted macro-source-note">数据截至 {bond.deadline_time}</p>
                ) : null}
                <p className="decision-muted macro-source-note">来源：南方基金债市晴雨表</p>
              </>
            ) : (
              <p className="decision-muted">{bond?.error || '债市数据暂不可用'}</p>
            )}
          </div>

          <div className="macro-section">
            <MarketRateIndicatorPanel rateIndicators={data?.rate_indicators} />
          </div>

          <div className="macro-section">
            <MarketStressPanel stress={data?.market_stress} segmentKey="bond" />
          </div>

          <div className="macro-section">
            <div className="macro-section-head">
              <div>
                <h3 className="macro-section-title">基准债指（静态表常用）</h3>
                <p className="decision-muted macro-section-sub">
                  中债网日频入库（非东财实时）。6 条债指各自独立 index_code，与静态表 benchmark 严格一一对应。
                </p>
              </div>
            </div>
            <MacroIndexGrid rows={data?.benchmark_bond} />
          </div>
        </>
      ) : null}

      {!loading && tab === 'equity' ? (
        <>
          <div className="macro-section">
            <EquityRegimeSummary regime={data?.equity_regime} />
          </div>
          <div className="macro-section">
            <MarketStressPanel stress={data?.market_stress} segmentKey="equity" />
          </div>
          {isSuper ? (
            <div className="macro-section macro-section-toolbar">
              <button type="button" className="decision-btn" onClick={() => setWatchlistOpen(true)}>
                添加指数
              </button>
            </div>
          ) : null}

          <div className="macro-section">
            <h3 className="macro-section-title">市场概览</h3>
            <MacroIndexGrid rows={data?.market_equity} {...manageProps} />
          </div>
          <div className="macro-section">
            <h3 className="macro-section-title">基准权益指数（静态表常用）</h3>
            <MacroIndexGrid rows={data?.benchmark_equity} {...manageProps} />
          </div>
          {data?.custom_equity?.length ? (
            <div className="macro-section">
              <h3 className="macro-section-title">自定义指数</h3>
              <MacroIndexGrid rows={data.custom_equity} {...manageProps} />
            </div>
          ) : null}
        </>
      ) : null}

      {data?.fetched_at ? (
        <p className="decision-muted macro-fetched-at">
          更新于 {new Date(data.fetched_at).toLocaleString('zh-CN', { hour12: false })}
        </p>
      ) : null}

      {isSuper ? (
        <MacroWatchlistModal
          open={watchlistOpen}
          onClose={() => setWatchlistOpen(false)}
          onChanged={loadSummary}
        />
      ) : null}
    </div>
  );
}
